import {Form, InputNumber, Radio, Space, Typography} from "antd";
import {FormikProps} from "formik";
import React, {FC, memo} from "react";
import {useCourseLateEntryPricingConfigurationQuery} from "../../../generated/graphql";
import {
    COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR,
    CourseLateEntryPricingFormFields,
    CourseLateEntryPricingModeValue,
    CourseMinimumLessonCountSource,
    TenantLateEntryPricingPolicyValue,
    isCourseLateEntryPricingMode,
    isTenantLateEntryPricingPolicy,
    isValidMinimumPartialBookingLessonCount,
} from "../courseLateEntryPricing";

type CourseLateEntryPricingSettingsProps = {
    formikProps: FormikProps<CourseLateEntryPricingFormFields>;
};

const CourseLateEntryPricingSettings: FC<
    CourseLateEntryPricingSettingsProps
> = ({formikProps}) => {
    const {data} = useCourseLateEntryPricingConfigurationQuery({
        // Die dedizierte Settings-Mutation aktualisiert diesen Public-Root-Key
        // nicht garantiert. Der Netzabruf verhindert eine veraltete Policy.
        fetchPolicy: "cache-and-network",
    });
    const configuration = data?.lateEntryPricingConfigurationPublic;

    const tenantPolicy: unknown = configuration?.lateEntryPricingPolicy;

    if (
        !isTenantLateEntryPricingPolicy(tenantPolicy) ||
        tenantPolicy !== TenantLateEntryPricingPolicyValue.Individual
    ) {
        return null;
    }

    const tenantMinimum = isValidMinimumPartialBookingLessonCount(
        configuration.minimumPartialBookingLessonCount,
    )
        ? configuration.minimumPartialBookingLessonCount
        : 1;
    const mode = isCourseLateEntryPricingMode(
        formikProps.values.lateEntryPricingMode,
    )
        ? formikProps.values.lateEntryPricingMode
        : CourseLateEntryPricingModeValue.OriginalPrice;
    const courseMinimum =
        formikProps.values.minimumPartialBookingLessonCount;
    const minimumSource =
        formikProps.values.minimumPartialBookingLessonCountSource ===
        CourseMinimumLessonCountSource.CourseOverride
            ? CourseMinimumLessonCountSource.CourseOverride
            : CourseMinimumLessonCountSource.TenantDefault;
    const minimumError =
        formikProps.errors.minimumPartialBookingLessonCount;
    const showMinimumError = Boolean(
        minimumSource === CourseMinimumLessonCountSource.CourseOverride &&
            !isValidMinimumPartialBookingLessonCount(courseMinimum),
    );

    return (
        <div
            data-testid="CourseLateEntryPricingSettings"
            style={{
                marginTop: 16,
                padding: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                background: "#fafafa",
            }}
        >
            <Typography.Text strong style={{display: "block", marginBottom: 4}}>
                {"Preis bei späterem Kurseinstieg"}
            </Typography.Text>
            <Typography.Paragraph
                type="secondary"
                style={{fontSize: 12, marginBottom: 12}}
            >
                {
                    "Diese Kursregel wird nur für neue Buchungen verwendet. Bestehende Buchungen bleiben unverändert."
                }
            </Typography.Paragraph>
            <Form.Item label="Preisberechnung nach Kursbeginn">
                <Radio.Group
                    aria-label="Preisberechnung nach Kursbeginn"
                    value={mode}
                    onChange={(event) => {
                        const nextMode: unknown = event.target.value;

                        if (isCourseLateEntryPricingMode(nextMode)) {
                            void formikProps.setFieldValue(
                                "lateEntryPricingMode",
                                nextMode,
                            );
                        }
                    }}
                >
                    <Space direction="vertical">
                        <Radio
                            value={
                                CourseLateEntryPricingModeValue.OriginalPrice
                            }
                        >
                            {"Originalpreis beibehalten"}
                        </Radio>
                        <Radio
                            value={
                                CourseLateEntryPricingModeValue.RemainingLessons
                            }
                        >
                            {"Preis an verbleibende Kurseinheiten anpassen"}
                        </Radio>
                    </Space>
                </Radio.Group>
            </Form.Item>
            <Form.Item label="Mindestanzahl buchbarer Kurseinheiten">
                <Radio.Group
                    aria-label="Mindestanzahl buchbarer Kurseinheiten"
                    value={minimumSource}
                    onChange={(event) => {
                        const nextSource: unknown = event.target.value;

                        if (
                            nextSource ===
                            CourseMinimumLessonCountSource.TenantDefault
                        ) {
                            void formikProps.setFieldValue(
                                "minimumPartialBookingLessonCountSource",
                                CourseMinimumLessonCountSource.TenantDefault,
                            );
                            void formikProps.setFieldValue(
                                "minimumPartialBookingLessonCount",
                                null,
                            );
                        } else if (
                            nextSource ===
                            CourseMinimumLessonCountSource.CourseOverride
                        ) {
                            void formikProps.setFieldValue(
                                "minimumPartialBookingLessonCountSource",
                                CourseMinimumLessonCountSource.CourseOverride,
                            );
                            void formikProps.setFieldValue(
                                "minimumPartialBookingLessonCount",
                                tenantMinimum,
                            );
                        }
                    }}
                >
                    <Space direction="vertical">
                        <Radio
                            value={CourseMinimumLessonCountSource.TenantDefault}
                        >
                            {`Standard aus den Einstellungen (${tenantMinimum}) verwenden`}
                        </Radio>
                        <Radio
                            value={CourseMinimumLessonCountSource.CourseOverride}
                        >
                            {"Eigene Mindestanzahl für diesen Kurs"}
                        </Radio>
                    </Space>
                </Radio.Group>
            </Form.Item>
            {minimumSource === CourseMinimumLessonCountSource.CourseOverride && (
                <Form.Item
                    label="Eigene Mindestanzahl"
                    validateStatus={showMinimumError ? "error" : undefined}
                    help={
                        showMinimumError
                            ? typeof minimumError === "string"
                                ? minimumError
                                : COURSE_MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_ERROR
                            : undefined
                    }
                >
                    <InputNumber
                        aria-label="Eigene Mindestanzahl"
                        min={1}
                        precision={0}
                        value={courseMinimum ?? undefined}
                        onChange={(value) =>
                            void formikProps.setFieldValue(
                                "minimumPartialBookingLessonCount",
                                value,
                            )
                        }
                        onBlur={() =>
                            void formikProps.setFieldTouched(
                                "minimumPartialBookingLessonCount",
                                true,
                            )
                        }
                    />
                </Form.Item>
            )}
        </div>
    );
};

export default memo(CourseLateEntryPricingSettings);
